
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Mail, MapPin, Phone } from "lucide-react";
import { PublicLayout } from "@/layouts/PublicLayout";
import { supabase } from "@/integrations/supabase/client";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validar dados do formulário
      if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        throw new Error("Todos os campos são obrigatórios");
      }

      console.log("Enviando formulário:", formData);
      
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      console.log("Resposta da função:", { data, error });

      if (error) {
        console.error("Erro na função Supabase:", error);
        throw new Error(error.message || "Erro ao enviar mensagem");
      }
      
      if (data?.error) {
        console.error("Erro no processamento:", data.error);
        throw new Error(data.error);
      }

      toast({
        title: "Mensagem enviada",
        description: "Agradecemos seu contato. Responderemos em breve!",
      });

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      toast({
        title: "Erro ao enviar mensagem",
        description: error.message || "Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6">Entre em Contato</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <div className="bg-card rounded-lg p-6 border flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-finance-turquoise/10 flex items-center justify-center text-finance-turquoise mb-4">
              <Mail className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-2">Email</h3>
            <p className="text-muted-foreground">contato@fincontrol.com</p>
            <p className="text-muted-foreground">suporte@fincontrol.com</p>
          </div>
          
          <div className="bg-card rounded-lg p-6 border flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-finance-turquoise/10 flex items-center justify-center text-finance-turquoise mb-4">
              <MapPin className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-2">Endereço</h3>
            <p className="text-muted-foreground">Av. Paulista, 1000</p>
            <p className="text-muted-foreground">São Paulo, SP</p>
          </div>
          
          <div className="bg-card rounded-lg p-6 border flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-finance-turquoise/10 flex items-center justify-center text-finance-turquoise mb-4">
              <Phone className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-2">Telefone</h3>
            <p className="text-muted-foreground">(11) 5555-5555</p>
            <p className="text-muted-foreground">(11) 9999-9999</p>
          </div>
        </div>
        
        <div className="bg-card rounded-lg p-6 border">
          <h2 className="text-xl font-semibold mb-6">Envie uma Mensagem</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">Nome</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Seu email"
                  required
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="subject" className="block text-sm font-medium mb-2">Assunto</label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Assunto da mensagem"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium mb-2">Mensagem</label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Sua mensagem"
                rows={5}
                required
              />
            </div>
            
            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
              {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
            </Button>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
